import { useEffect, useState } from "react";
import { useBarcodeScan } from "src/hooks/useBarcodeScan";
import { usePDA1123_R01Content } from "./PDA1123_R01Content";
import usePDA1123_R01Store from "./usePDA1123_R01Store";

const KeyboardIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const PDA1123_R01Info = () => {
  const { lotInput, setLotInput, scanResult, recoverList } = usePDA1123_R01Store();
  const { handleScanLot, handleRecover, handleRegister } = usePDA1123_R01Content();
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [lastScanned, setLastScanned] = useState("");
  const [isScanning, setIsScanning] = useState(false);

  const handleBarcodeScan = (scannedValue: string) => {
    if (isScanning || lastScanned === scannedValue) return;
    setIsScanning(true);
    setLastScanned(scannedValue);
    void handleScanLot(scannedValue);
    setTimeout(() => {
      setIsScanning(false);
      setLastScanned("");
      scanInputRef.current?.focus();
    }, 400);
  };

  const { scanInputRef, handleScanKeyDown, handleInput } = useBarcodeScan(handleBarcodeScan, {
    clearOnScan: false,
    refocusOnScan: true,
    debounceMs: 100,
    autoFocus: true,
  });

  useEffect(() => {
    scanInputRef.current?.focus();
  }, [scanInputRef]);

  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapTime < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }
    setLastTapTime(now);
  };

  const toggleKeyboard = () => {
    setKeyboardEnabled((enabled) => !enabled);
    if (!keyboardEnabled) setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  return (
    <div className="w-full bg-white p-4 pb-1 border-b border-gray-200 overflow-y-auto">
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">LOT No</label>
          <div className="flex-1 flex gap-1">
            <input
              ref={scanInputRef}
              type="text"
              value={lotInput}
              onChange={(event) => setLotInput(event.target.value)}
              onInput={handleInput}
              onKeyDown={handleScanKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              autoComplete="off"
              placeholder="LOT 번호 스캔"
              className={`flex-1 max-w-[210px] px-2 text-lg border rounded text-black focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                scanResult ? "bg-green-50 border-green-500" : "bg-white border-gray-300"
              }`}
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              aria-label="키보드 입력 전환"
              className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                keyboardEnabled ? "bg-blue-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              <KeyboardIcon />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목</label>
          <input
            type="text"
            value={scanResult?.itemNm || ""}
            readOnly
            className={`flex-1 px-2 text-lg border rounded text-black ${
              scanResult ? "border-green-400 bg-green-50" : "border-gray-300 bg-gray-100"
            }`}
          />
        </div>
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">규격</label>
          <input
            type="text"
            value={scanResult?.itemSpec || ""}
            readOnly
            className={`flex-1 px-2 text-lg border rounded text-black ${
              scanResult ? "border-green-400 bg-green-50" : "border-gray-300 bg-gray-100"
            }`}
          />
        </div>
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">LOT재고</label>
          <input
            type="text"
            value={scanResult ? scanResult.lotQty.toLocaleString() : ""}
            readOnly
            placeholder="ea"
            className={`flex-1 px-2 text-lg border rounded text-black ${
              scanResult ? "border-green-400 bg-green-50" : "border-gray-300 bg-gray-100"
            }`}
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleRecover}
            disabled={!scanResult}
            className="flex-1 py-2 text-lg bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            자재회수
          </button>
          <button
            onClick={() => void handleRegister()}
            disabled={recoverList.length === 0}
            className="flex-1 py-2 text-lg bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            등록 ({recoverList.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDA1123_R01Info;
